import { Quick, Rule, Species, SpeciesSummary, Waters } from './regs-models';

export type VerdictKind = 'keep' | 'closed' | 'release' | 'info';

export interface Verdict {
  readonly kind: VerdictKind;
  readonly label: string; // "KEEP TODAY", "CLOSED TODAY", ...
  readonly big?: string; // "10"
  readonly unit: string; // "per person"
  readonly detail: string; // "12.5 in TL minimum · this limit runs through Oct 31"
  readonly says: string; // what Skipper says in his bubble
  readonly note?: string | null; // amber note: what changes next
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAY_NAMES: Record<string, string> = {
  Mon: 'Mondays',
  Tue: 'Tuesdays',
  Wed: 'Wednesdays',
  Thu: 'Thursdays',
  Fri: 'Fridays',
  Sat: 'Saturdays',
  Sun: 'Sundays',
};

/**
 * "10-31" -> "Oct 31"
 */
export function mdLabel(md?: string | null): string {
  if (!md || md.length !== 5) return md || '';
  const m = parseInt(md.substring(0, 2), 10);
  const d = parseInt(md.substring(3), 10);
  if (isNaN(m) || isNaN(d) || m < 1 || m > 12) return md;
  return `${MON[m - 1]} ${d}`;
}

export function closureNames(labels: string[]): string {
  return labels
    .map((l) => l.replace(/\s*[—-]?\s*\bclosed\b\s*$/i, ''))
    .join(', ');
}

/**
 * A size limit as a number of inches plus how it's measured. "<73 in CFL" -> (73, 'CFL', under: true)
 */
export class SizeLimit {
  readonly inches: number;
  readonly measure: string; // TL, CFL, LJFL, FL or ''
  readonly under: boolean;

  constructor(inches: number, measure: string, under = false) {
    this.inches = inches;
    this.measure = measure;
    this.under = under;
  }

  static parse(s?: string | null): SizeLimit | null {
    if (!s) return null;
    let m = s.match(/^\s*(<)?\s*(\d+(?:\.\d+)?)\s*in\b\s*([A-Z]{2,4})?/);
    if (!m) {
      m = s.match(/^\s*(<)?\s*(\d+(?:\.\d+)?)()\s*$/);
    }
    if (!m) return null;
    return new SizeLimit(parseFloat(m[2]), m[3] || '', m[1] != null);
  }

  get number(): string {
    return this.inches === Math.round(this.inches) ? this.inches.toFixed(0) : `${this.inches}`;
  }

  get text(): string {
    return `${this.under ? 'under ' : ''}${this.number} in${this.measure ? ' ' + this.measure : ''}`;
  }
}

/**
 * "12.5 in TL minimum", "28 to 31 in TL", "27 to under 73 in CFL", or '' when there is no size limit.
 */
export function sizeText(q: Quick): string {
  const lo = SizeLimit.parse(q.min);
  const hi = SizeLimit.parse(q.max);
  if (lo != null && hi != null) {
    const m = lo.measure.length > 0 ? lo.measure : hi.measure;
    return `${lo.number} to ${hi.under ? 'under ' : ''}${hi.number} in${m.length > 0 ? ' ' + m : ''}`;
  }
  if (lo != null) return `${lo.text} minimum`;
  if (hi != null) return `${hi.text} maximum`;
  return '';
}

/**
 * Bag number and its unit: "10 per person" -> ('10', 'per person'); "1 fish/vessel/day" -> ('1', 'fish per vessel per day').
 */
export function splitBag(bag: string): [string | undefined, string] {
  const m = bag.trim().match(/^(\d{1,3})(?![\d,])\s*(.*)$/);
  if (!m || m[2].startsWith('lb')) return [undefined, bag];
  let unit = m[2].replace(/\s*\/\s*/g, ' per ').trim();
  if (unit.length === 0) unit = 'fish';
  return [m[1], unit];
}

function inRange(md: string, from: string, to: string): boolean {
  return from <= to ? md >= from && md <= to : md >= from || md <= to;
}

export function nextChange(rule: Rule, md: string): { from: string; label: string | null } | null {
  const ahead = rule.seasons
    .filter((s) => s.from > md && !s.closed)
    .sort((a, b) => a.from.localeCompare(b.from));
  return ahead.length === 0 ? null : { from: ahead[0].from, label: ahead[0].label };
}

export function runsThrough(rule: Rule, md: string): string | null {
  const cur = rule.seasons.filter((s) => inRange(md, s.from, s.to) && !s.closed);
  if (cur.length === 0) return null;
  cur.sort((a, b) => a.to.localeCompare(b.to));
  return mdLabel(cur[0].to);
}

function noRetentionNote(rule: Rule, md: string): string | null {
  for (const n of rule.noRetentionDays) {
    if (!inRange(md, n.from, n.to)) continue;
    const days = n.weekdays.map((d) => DAY_NAMES[d] || d);
    const list =
      days.length > 1
        ? `${days.slice(0, days.length - 1).join(', ')} and ${days[days.length - 1]}`
        : days.join();
    return `No retention on ${list} through ${mdLabel(n.to)}.`;
  }
  return null;
}

/**
 * Skipper's verdict for one rule today. [todayMd] = NJ date "MM-DD" from the API (species.todayMd).
 */
export function verdictFor(sp: Species, rule: Rule, w: Waters, todayMd: string): Verdict {
  const s = sp.short;
  const t = rule.today;
  const q = rule.quick;
  const where = w.label;
  const size = sizeText(q);
  const next = nextChange(rule, todayMd);
  const nextNote = next == null ? null : `From ${mdLabel(next.from)}: ${next.label || 'new limits'}.`;

  switch (t.status) {
    case 'prohibited':
      return {
        kind: 'release',
        label: 'NO RETENTION',
        unit: 'No retention',
        detail: where,
        says: `No, release it. ${s} can’t be kept in ${w.lowerLabel}.`,
      };
    case 'catch_and_release':
      return {
        kind: 'release',
        label: 'CATCH AND RELEASE',
        unit: 'Catch & release',
        detail: where,
        says: 'Catch and release only. Let it go quickly.',
        note: nextNote,
      };
    case 'not_applicable':
      return {
        kind: 'info',
        label: 'CHECK THE RULES',
        unit: q.bag || 'Check rules',
        detail: q.bag,
        says: `${q.bag}. Tap below for the details.`,
      };
    case 'closed': {
      const re = t.nextFrom
        ? ` It reopens ${mdLabel(t.nextFrom)}${t.nextLabel ? ` (${t.nextLabel})` : ''}.`
        : '';
      return {
        kind: 'closed',
        label: 'CLOSED TODAY',
        unit: 'Closed',
        detail: size.length === 0 ? where : `${size} when open`,
        says: `Not right now. ${s} is closed in ${w.lowerLabel}.${re}`,
      };
    }
  }

  // open
  if (t.noRetention && t.noRetention.length > 0) {
    return {
      kind: 'closed',
      label: 'NO RETENTION TODAY',
      unit: 'No retention',
      detail: t.noRetention,
      says: `Not today. ${t.noRetention}.`,
      note: noRetentionNote(rule, todayMd),
    };
  }

  const labelFromCurrent = t.current
    .map((l) => {
      const match = l.match(/^\s*(\d{1,3})\s+fish\b/);
      return match ? match[1] : null;
    })
    .filter((x): x is string => x != null);

  let [big, unit] = splitBag(q.bag);
  if (labelFromCurrent.length > 0) {
    big = labelFromCurrent[0];
  }
  const through = runsThrough(rule, todayMd);
  const detailParts: string[] = [];
  if (size.length > 0) detailParts.push(size);
  if (through) detailParts.push(`this limit runs through ${through}`);
  const detail = detailParts.join(' · ');

  const noRet = noRetentionNote(rule, todayMd);
  const closedNow = t.closures.length === 0 ? null : `Today: ${closureNames(t.closures)} closed.`;
  const noteParts: string[] = [];
  if (closedNow) noteParts.push(closedNow);
  if (noRet) noteParts.push(noRet);
  if (nextNote) noteParts.push(nextNote);
  const note = noteParts.join(' ');

  const bagWords = big == null ? `Limit: ${q.bag}.` : `You can keep ${big} ${unit} today.`;
  return {
    kind: 'keep',
    label: 'KEEP TODAY',
    big,
    unit: big == null ? q.bag : unit,
    detail,
    says: size.length === 0
      ? `Yes. ${bagWords}`
      : `Yes, if it’s ${size.replace(' minimum', ' or longer')}. ${bagWords}`,
    note: note.length > 0 ? note : null,
  };
}

export interface AskMatch {
  readonly species: SpeciesSummary;
  readonly inches: number | null;
}

export function parseAsk(query: string, all: SpeciesSummary[]): AskMatch | null {
  const q = ` ${query.toLowerCase().replace(/[^a-z0-9.\s"”-]/g, ' ')} `;
  let best: SpeciesSummary | null = null;
  let bestLen = 0;

  for (const s of all) {
    const names = new Set([s.name, s.short, ...s.aka]);
    for (const n of names) {
      const name = n.toLowerCase().trim();
      if (name.length < 3) continue;
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(^|\\s)${escaped}(s)?(\\s|$)`, 'i');
      if (regex.test(q) && name.length > bestLen) {
        best = s;
        bestLen = name.length;
      }
    }
  }

  if (!best) return null;

  const m = q.match(/(\d+(?:\.\d+)?)\s*(?:"|”|in\b|inch(?:es)?\b|-inch\b)/i);
  return {
    species: best,
    inches: m ? parseFloat(m[1]) : null,
  };
}

export function answerSize(sp: Species, rule: Rule, w: Waters, inches: number): string {
  const s = sp.short;
  const t = rule.today;
  const q = rule.quick;
  const n = inches === Math.round(inches) ? inches.toFixed(0) : `${inches}`;

  if (t.status === 'prohibited' || t.status === 'catch_and_release') {
    return `Release it. ${s} can’t be kept in ${w.lowerLabel} at any size.`;
  }
  if (t.status === 'closed') {
    return `${s} is closed in ${w.lowerLabel} right now, so release it whatever the size.`;
  }
  if (t.noRetention && t.noRetention.length > 0) {
    return `Not today: ${t.noRetention}.`;
  }

  const lo = SizeLimit.parse(q.min);
  const hi = SizeLimit.parse(q.max);
  if (!lo && !hi) {
    return `There’s no simple size limit for ${s.toLowerCase()} here. Check the details below.`;
  }
  if (lo && inches < lo.inches) {
    return `Too short. A ${n} inch ${s.toLowerCase()} is under the ${lo.text} minimum. Release it.`;
  }
  if (hi && (hi.under ? inches >= hi.inches : inches > hi.inches)) {
    return `Too big. The limit is ${sizeText(q)}. Release it.`;
  }

  const [big, unit] = splitBag(q.bag);
  return `Yes, a ${n} inch ${s.toLowerCase()} is a keeper (${sizeText(q)}). ${
    big == null ? `Limit: ${q.bag}.` : `Limit: ${big} ${unit}.`
  }`;
}
